import React, { useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { IconCheck, IconX, IconCalendarEvent, IconList, IconCalendarStats } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../context/LoadingContext';
import { todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';
import { LEAVE_TYPES, buildLeaveViewData } from '../utils/leaves';
import { holidaysForList } from '../utils/holidays';
import Layout from '../components/Layout';
import SelectField from '../components/SelectField';
import ConfirmModal from '../components/ConfirmModal';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

const STATUS_STYLES = {
  Pending: 'bg-neutral-bg text-neutral-tx',
  Approved: 'bg-success-bg text-success-tx',
  Disapproved: 'bg-error-bg text-error-tx',
  Cancelled: 'bg-neutral-bg text-neutral-tx',
};

// "0.5 (AM)" for a half day, otherwise just the plain day count — shared by
// every table/modal that shows a request's duration.
function formatDuration(l) {
  return l.halfDayPeriod ? `${l.days} (${l.halfDayPeriod})` : String(l.days);
}

function LeaveStatusPill({ status }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full font-medium px-2.5 py-0.5 text-[11px] tracking-wide ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

// Same inline re-auth pattern as Check In/Out: instead of a modal's normal
// Confirm/Cancel, show a quick Google sign-in right there when the token's
// gone stale, so people don't have to leave the page to fix it.
function ReauthFooter({ setIdToken, onCancel }) {
  const { showError } = useToast();
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted text-center">Your session needs a quick refresh before this can be saved.</p>
      <GoogleLogin
        onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
        onError={() => showError('Google sign-in failed — please try again.')}
      />
      <button onClick={onCancel} className="text-sm font-medium text-muted hover:text-ink transition-colors">
        Cancel
      </button>
    </div>
  );
}

export default function Leaves() {
  const {
    currentUser,
    users,
    leaves: rawLeaves,
    leaveBalances,
    holidays,
    holidayListItems,
    fileLeave,
    cancelLeave,
    decideLeave,
    adjustBalance,
    setIdToken,
    isIdTokenValid,
  } = useApp();
  const { showSuccess, showError } = useToast();
  const { withLoading } = useLoading();
  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;

  const todayCst = useMemo(() => todayDateStringInTz(DAY_ANCHOR_TZ), []);
  const { leaves, balances } = useMemo(
    () => buildLeaveViewData(users, rawLeaves, leaveBalances),
    [users, rawLeaves, leaveBalances]
  );

  const [mainTab, setMainTab] = useState('self');
  const isAdmin = Boolean(currentUser && currentUser.isAdmin);
  const pendingCount = leaves.filter((l) => l.status === 'Pending').length;

  const myBalance = currentUser ? balances[currentUser.uuid] : null;
  const myLeaves = currentUser
    ? leaves.filter((l) => l.uuid === currentUser.uuid).sort((a, b) => (a.dateFrom < b.dateFrom ? 1 : -1))
    : [];
  const myHolidays = useMemo(
    () => (currentUser && currentUser.holiday_list_id ? holidaysForList(currentUser.holiday_list_id, holidays, holidayListItems) : []),
    [currentUser, holidays, holidayListItems]
  );

  // File-a-leave form state
  const [leaveType, setLeaveType] = useState(LEAVE_TYPES[0]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dayPortion, setDayPortion] = useState('Full day');
  const [reason, setReason] = useState('');

  // Half-day only makes sense for a single-day request — a multi-day range
  // has no "half" of itself. Whenever the dates stop being the same day,
  // fall back to Full day rather than leave a stale Half day selection that
  // no longer has a control visible to change it.
  const isSingleDay = Boolean(dateFrom && dateTo && dateFrom === dateTo);

  // Filing isn't blocked by balance (someone might file ahead of a future
  // credit grant, or an admin might override at approval time) — but it's
  // still worth flagging up front rather than only finding out at review.
  const isHalfDaySelected = isSingleDay && (dayPortion === 'AM' || dayPortion === 'PM');
  const requestedDays =
    dateFrom && dateTo
      ? isHalfDaySelected
        ? 0.5
        : Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1
      : 0;
  const exceedsBalance = Boolean(myBalance) && requestedDays > myBalance.remaining;

  const handleDateChange = (field, value) => {
    let nextFrom = dateFrom;
    let nextTo = dateTo;
    if (field === 'from') {
      nextFrom = value;
      setDateFrom(value);
      // Keep the range valid — if "From" moves past the current "To",
      // pull "To" up to match rather than leaving a stale, invalid range.
      if (dateTo && value > dateTo) {
        nextTo = value;
        setDateTo(value);
      }
    } else {
      nextTo = value;
      setDateTo(value);
    }
    if (!(nextFrom && nextTo && nextFrom === nextTo)) setDayPortion('Full day');
  };

  // Submitting is a two-step flow now: validate first, then a confirm modal
  // (matching approve/disapprove/cancel) actually files it.
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  const handleSubmit = () => {
    if (!leaveType || !dateFrom || !dateTo) {
      showError('Pick a leave type and both dates first.');
      return;
    }
    if (dateTo < dateFrom) {
      showError('"To" date must be on or after "From" date.');
      return;
    }
    setSubmitConfirmOpen(true);
  };

  const confirmSubmit = async () => {
    try {
      await withLoading(() =>
        fileLeave({
          leaveType,
          dateFrom,
          dateTo,
          isHalfDay: isHalfDaySelected,
          halfDayPeriod: isHalfDaySelected ? dayPortion : undefined,
          reason,
        })
      );
      showSuccess('Leave request submitted.');
      setSubmitConfirmOpen(false);
      setDateFrom('');
      setDateTo('');
      setReason('');
      setDayPortion('Full day');
    } catch (err) {
      showError(err.message || "Couldn't submit that — please try again.");
    }
  };

  const [cancelTarget, setCancelTarget] = useState(null); // leaveId or null
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await withLoading(() => cancelLeave({ leaveId: cancelTarget }));
      showSuccess('Request cancelled.');
      setCancelTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't cancel that — please try again.");
    }
  };

  const handleDecide = async ({ leaveId, decision, decisionNotes }) => {
    await withLoading(() => decideLeave({ leaveId, decision, decisionNotes }));
    showSuccess(`Request ${decision.toLowerCase()}.`);
  };

  const handleAdjustBalance = async ({ targetUuid, creditsRemaining }) => {
    await withLoading(() => adjustBalance({ targetUuid, creditsRemaining }));
    showSuccess('Balance updated.');
  };

  if (!currentUser) return null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-ink">Leaves</h1>
            <p className="text-xs sm:text-sm text-muted mt-1">File a leave, track its status, and see your remaining credits.</p>
          </div>

          {isAdmin && (
            <div className="inline-flex rounded-lg border border-border bg-card p-0.5 text-xs font-semibold">
              <button
                onClick={() => setMainTab('self')}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  mainTab === 'self' ? 'bg-gold text-white' : 'text-muted hover:text-ink'
                }`}
              >
                My Leaves
              </button>
              <button
                onClick={() => setMainTab('admin')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
                  mainTab === 'admin' ? 'bg-gold text-white' : 'text-muted hover:text-ink'
                }`}
              >
                Admin
                {pendingCount > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[10px] font-bold ${
                      mainTab === 'admin' ? 'bg-white text-gold' : 'bg-error-tx text-white'
                    }`}
                  >
                    {pendingCount}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {(!isAdmin || mainTab === 'self') && (
          <SelfView
            balance={myBalance}
            myLeaves={myLeaves}
            myHolidays={myHolidays}
            todayCst={todayCst}
            leaveType={leaveType}
            setLeaveType={setLeaveType}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={handleDateChange}
            isSingleDay={isSingleDay}
            dayPortion={dayPortion}
            setDayPortion={setDayPortion}
            reason={reason}
            setReason={setReason}
            onSubmit={handleSubmit}
            requestedDays={requestedDays}
            exceedsBalance={exceedsBalance}
            submitConfirmOpen={submitConfirmOpen}
            onConfirmSubmit={confirmSubmit}
            onCloseSubmitConfirm={() => setSubmitConfirmOpen(false)}
            cancelTarget={cancelTarget}
            onOpenCancel={setCancelTarget}
            onConfirmCancel={confirmCancel}
            onCloseCancel={() => setCancelTarget(null)}
            needsReauth={needsReauth}
            setIdToken={setIdToken}
          />
        )}

        {isAdmin && mainTab === 'admin' && (
          <AdminView
            users={users}
            leaves={leaves}
            balances={balances}
            todayCst={todayCst}
            onDecide={handleDecide}
            onAdjustBalance={handleAdjustBalance}
            needsReauth={needsReauth}
            setIdToken={setIdToken}
          />
        )}
      </div>
    </Layout>
  );
}

function SelfView({
  balance,
  myLeaves,
  myHolidays,
  todayCst,
  leaveType,
  setLeaveType,
  dateFrom,
  dateTo,
  onDateChange,
  isSingleDay,
  dayPortion,
  setDayPortion,
  reason,
  setReason,
  onSubmit,
  requestedDays,
  exceedsBalance,
  submitConfirmOpen,
  onConfirmSubmit,
  onCloseSubmitConfirm,
  cancelTarget,
  onOpenCancel,
  onConfirmCancel,
  onCloseCancel,
  needsReauth,
  setIdToken,
}) {
  return (
    <>
      <div className="bg-card border border-border rounded-2xl shadow-sm p-4 sm:p-6 mt-4">
        <p className="text-xs font-medium text-muted uppercase tracking-wide">Your balance</p>
        {balance ? (
          <>
            <p className="text-2xl sm:text-3xl font-semibold text-ink mt-1">
              {balance.remaining} <span className="text-sm font-medium text-muted">credits remaining</span>
            </p>
            <p className="text-xs text-muted mt-1">
              {balance.used} used of {balance.total} this year
            </p>
          </>
        ) : (
          <p className="text-sm text-muted mt-1">No balance on file yet.</p>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-sm p-4 sm:p-6 mt-4">
        <h2 className="text-sm font-semibold text-ink">File a leave</h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Type</label>
            <SelectField value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
              {LEAVE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </SelectField>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Duration</label>
            {isSingleDay ? (
              <div className="inline-flex rounded-lg border border-border bg-card p-0.5 text-xs font-semibold">
                {['Full day', 'AM', 'PM'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setDayPortion(option)}
                    className={`px-3 py-1.5 rounded-md transition-colors ${
                      dayPortion === option ? 'bg-gold text-white' : 'text-muted hover:text-ink'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted mt-1.5">Pick the same "From" and "To" date to file a half day.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateChange('from', e.target.value)}
              className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => onDateChange('to', e.target.value)}
              className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Anything your admin should know"
            className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
          />
        </div>
        <button
          onClick={onSubmit}
          className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors text-sm"
        >
          <IconCalendarEvent size={16} stroke={1.8} />
          Submit request
        </button>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-sm mt-6 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-ink">My requests</h2>
        </div>
        {myLeaves.length === 0 ? (
          <p className="text-xs text-muted italic p-4 sm:p-6">No requests filed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-light-gold text-left">
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Dates</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Type</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Days</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide">Reason / notes</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {myLeaves.map((l) => (
                  <tr key={l.leaveId} className="border-t border-border align-top hover:bg-card-tint transition-colors">
                    <td className="px-3 py-2.5 whitespace-nowrap text-ink">
                      {l.dateFrom === l.dateTo ? l.dateFrom : `${l.dateFrom} → ${l.dateTo}`}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-ink">{l.leaveType}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-ink">{formatDuration(l)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <LeaveStatusPill status={l.status} />
                    </td>
                    <td className="px-3 py-2.5 text-ink min-w-[14rem]">
                      <p>{l.reason}</p>
                      {l.decisionNotes && (
                        <p className="text-xs text-muted italic mt-1">Admin: {l.decisionNotes}</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {l.status === 'Pending' && (
                        <button
                          onClick={() => onOpenCancel(l.leaveId)}
                          className="text-xs font-medium text-bronze hover:text-ink transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CalendarGrid
        leaves={myLeaves}
        holidays={myHolidays}
        todayCst={todayCst}
        statuses={['Approved']}
        chipLabel={(l) => l.leaveType.replace(' Leave', '')}
      />

      <ConfirmModal
        open={submitConfirmOpen}
        title="Submit this leave request?"
        confirmLabel="Submit"
        onConfirm={onConfirmSubmit}
        onCancel={onCloseSubmitConfirm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={onCloseSubmitConfirm} /> : undefined}
      >
        <p>
          <span className="font-medium">{leaveType}</span>
          {isSingleDay && (dayPortion === 'AM' || dayPortion === 'PM') ? ` (half day, ${dayPortion})` : ''} —{' '}
          {dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`}
        </p>
        {exceedsBalance && (
          <p className="mt-2 rounded-lg bg-info-bg text-info-tx px-3 py-2 text-sm font-medium">
            This is {requestedDays} day{requestedDays === 1 ? '' : 's'}, more than your {balance ? balance.remaining : 0}{' '}
            remaining — you can still submit it, but it can't be approved until your balance covers it.
          </p>
        )}
        {reason && <p className="mt-2 text-sm text-muted">"{reason}"</p>}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(cancelTarget)}
        title="Cancel this request?"
        confirmLabel="Cancel request"
        onConfirm={onConfirmCancel}
        onCancel={onCloseCancel}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={onCloseCancel} /> : undefined}
      >
        <p>This withdraws the request — you can always file a new one.</p>
      </ConfirmModal>
    </>
  );
}

function AdminView({ users, leaves, balances, todayCst, onDecide, onAdjustBalance, needsReauth, setIdToken }) {
  const { showError } = useToast();
  const [subTab, setSubTab] = useState('list');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [personFilter, setPersonFilter] = useState('all');
  const [decisionTarget, setDecisionTarget] = useState(null); // { leave, decision: 'Approved' | 'Disapproved' }
  const [remarks, setRemarks] = useState('');
  const [balanceTarget, setBalanceTarget] = useState(null); // user object
  const [totalInput, setTotalInput] = useState('');
  const [remainingInput, setRemainingInput] = useState('');

  const openDecision = (leave, decision) => {
    setDecisionTarget({ leave, decision });
    setRemarks('');
  };
  const closeDecision = () => setDecisionTarget(null);
  const confirmDecision = async () => {
    if (!decisionTarget) return;
    try {
      await onDecide({
        leaveId: decisionTarget.leave.leaveId,
        decision: decisionTarget.decision,
        decisionNotes: remarks,
      });
      closeDecision();
    } catch (err) {
      showError(err.message || "Couldn't save that decision — please try again.");
    }
  };

  const openBalanceAdjust = (user, currentTotal, currentRemaining) => {
    setBalanceTarget(user);
    setTotalInput(String(currentTotal));
    setRemainingInput(String(currentRemaining));
  };
  const closeBalanceAdjust = () => setBalanceTarget(null);
  const confirmBalanceAdjust = async () => {
    if (!balanceTarget) return;
    const total = Number(totalInput);
    const remaining = Number(remainingInput);
    if (isNaN(total) || total < 0 || isNaN(remaining) || remaining < 0) {
      showError('Enter numbers of 0 or more.');
      return;
    }
    if (remaining > total) {
      showError("Remaining can't be more than total.");
      return;
    }
    try {
      await onAdjustBalance({ targetUuid: balanceTarget.uuid, creditsTotal: total, creditsRemaining: remaining });
      closeBalanceAdjust();
    } catch (err) {
      showError(err.message || "Couldn't save that balance — please try again.");
    }
  };

  const personFilteredLeaves = useMemo(
    () => (personFilter === 'all' ? leaves : leaves.filter((l) => l.uuid === personFilter)),
    [leaves, personFilter]
  );

  const filteredLeaves = useMemo(() => {
    const sorted = [...personFilteredLeaves].sort((a, b) => (a.dateFrom < b.dateFrom ? 1 : -1));
    if (statusFilter === 'All') return sorted;
    return sorted.filter((l) => l.status === statusFilter);
  }, [personFilteredLeaves, statusFilter]);

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5 text-xs font-semibold">
          <button
            onClick={() => setSubTab('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
              subTab === 'list' ? 'bg-gold text-white' : 'text-muted hover:text-ink'
            }`}
          >
            <IconList size={14} stroke={2} />
            List
          </button>
          <button
            onClick={() => setSubTab('calendar')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
              subTab === 'calendar' ? 'bg-gold text-white' : 'text-muted hover:text-ink'
            }`}
          >
            <IconCalendarStats size={14} stroke={2} />
            Calendar
          </button>
        </div>

        <div className="w-48">
          <SelectField value={personFilter} onChange={(e) => setPersonFilter(e.target.value)} className="!py-1 !text-xs">
            <option value="all">All VAs</option>
            {users.map((u) => (
              <option key={u.uuid} value={u.uuid}>
                {u.name} {u.isAdmin ? '(Admin)' : ''}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      {subTab === 'list' ? (
        <>
          <div className="bg-card border border-border rounded-2xl shadow-sm mt-4 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink">Requests</h2>
              <div className="w-36">
                <SelectField
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="!py-1 !text-xs"
                >
                  {['Pending', 'Approved', 'Disapproved', 'Cancelled', 'All'].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
            {filteredLeaves.length === 0 ? (
              <p className="text-xs text-muted italic p-4 sm:p-6">Nothing here.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-light-gold text-left">
                      <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Employee</th>
                      <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Dates</th>
                      <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Type</th>
                      <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Days</th>
                      <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                      <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide">Reason</th>
                      <th className="px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeaves.map((l) => {
                      const bal = balances[l.uuid];
                      return (
                        <tr key={l.leaveId} className="border-t border-border align-top hover:bg-card-tint transition-colors">
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <p className="font-medium text-ink">{l.employee}</p>
                            <p className="text-[10px] text-muted uppercase tracking-wide">
                              {bal ? `${bal.remaining} left` : ''}
                            </p>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-ink">
                            {l.dateFrom === l.dateTo ? l.dateFrom : `${l.dateFrom} → ${l.dateTo}`}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-ink">{l.leaveType}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-ink">{formatDuration(l)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <LeaveStatusPill status={l.status} />
                          </td>
                          <td className="px-3 py-2.5 text-ink min-w-[12rem]">
                            <p>{l.reason}</p>
                            {l.decisionNotes && (
                              <p className="text-xs text-muted italic mt-1">{l.decisionNotes}</p>
                            )}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {l.status === 'Pending' && (
                              <div className="flex gap-1.5">
                                <button
                                  onClick={() => openDecision(l, 'Approved')}
                                  title="Approve"
                                  className="flex items-center justify-center w-7 h-7 rounded-lg bg-success-bg text-success-tx hover:opacity-80 transition-opacity"
                                >
                                  <IconCheck size={14} stroke={2.2} />
                                </button>
                                <button
                                  onClick={() => openDecision(l, 'Disapproved')}
                                  title="Disapprove"
                                  className="flex items-center justify-center w-7 h-7 rounded-lg bg-error-bg text-error-tx hover:opacity-80 transition-opacity"
                                >
                                  <IconX size={14} stroke={2.2} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-2xl shadow-sm mt-6 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-border">
              <h2 className="text-sm font-semibold text-ink">Everyone's balance</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-light-gold text-left">
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Employee</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Total</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Used</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Remaining</th>
                    <th className="px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const bal = balances[u.uuid] || { remaining: 0, used: 0, total: 0 };
                    return (
                      <tr key={u.uuid} className="border-t border-border hover:bg-card-tint transition-colors">
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <p className="font-medium text-ink">{u.name}</p>
                          <p className="text-[10px] text-muted uppercase tracking-wide">{u.isAdmin ? 'Admin' : 'VA'}</p>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-ink">{bal.total}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-ink">{bal.used}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-ink font-medium">{bal.remaining}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <button
                            onClick={() => openBalanceAdjust(u, bal.total, bal.remaining)}
                            className="text-xs font-medium text-bronze hover:text-ink transition-colors"
                          >
                            Adjust
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <CalendarGrid
          leaves={personFilteredLeaves}
          todayCst={todayCst}
          chipLabel={(l) => `${l.employee} - ${l.leaveType.replace(' Leave', '')} - ${l.halfDayPeriod || 'Full'}`}
        />
      )}

      <ConfirmModal
        open={Boolean(decisionTarget)}
        title={decisionTarget ? `${decisionTarget.decision === 'Approved' ? 'Approve' : 'Disapprove'} this leave?` : ''}
        confirmLabel={decisionTarget ? (decisionTarget.decision === 'Approved' ? 'Approve' : 'Disapprove') : 'Confirm'}
        onConfirm={confirmDecision}
        onCancel={closeDecision}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeDecision} /> : undefined}
      >
        {decisionTarget && (
          <>
            <p>
              <span className="font-medium">{decisionTarget.leave.employee}</span> —{' '}
              {decisionTarget.leave.leaveType},{' '}
              {decisionTarget.leave.dateFrom === decisionTarget.leave.dateTo
                ? decisionTarget.leave.dateFrom
                : `${decisionTarget.leave.dateFrom} → ${decisionTarget.leave.dateTo}`}{' '}
              ({formatDuration(decisionTarget.leave)} day{decisionTarget.leave.days === 1 ? '' : 's'})
            </p>
            <div className="mt-3">
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
                Remarks (optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                placeholder={
                  decisionTarget.decision === 'Approved'
                    ? 'Anything the employee should know'
                    : 'Let them know why, if it helps'
                }
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
              />
            </div>
          </>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(balanceTarget)}
        title={balanceTarget ? `Adjust ${balanceTarget.name}'s balance` : ''}
        confirmLabel="Save"
        onConfirm={confirmBalanceAdjust}
        onCancel={closeBalanceAdjust}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeBalanceAdjust} /> : undefined}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
              Total (yearly grant)
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={totalInput}
              onChange={(e) => setTotalInput(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
              Remaining (current)
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={remainingInput}
              onChange={(e) => setRemainingInput(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-muted">On first setup these are usually the same number.</p>
      </ConfirmModal>
    </div>
  );
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CALENDAR_CHIP_LIMIT = 3;

// Traditional month-grid calendar (Monday-start, 6 weeks) — each day cell
// lists whoever's on leave that day as a small chip. Always exactly 7
// columns regardless of month length, which is what makes this responsive
// down to phone width without needing horizontal scroll, unlike a
// day-per-column spreadsheet layout would be.
function CalendarGrid({
  leaves,
  holidays = [],
  todayCst,
  statuses = ['Approved', 'Pending'],
  chipLabel = (l) => l.employee.split(' ')[0],
}) {
  const [monthDate, setMonthDate] = useState(() => `${todayCst.slice(0, 7)}-01`);

  const year = Number(monthDate.slice(0, 4));
  const monthIndex = Number(monthDate.slice(5, 7)) - 1;
  const monthLabel = new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const shiftMonth = (delta) => {
    const d = new Date(Date.UTC(year, monthIndex + delta, 1));
    setMonthDate(`${d.toISOString().slice(0, 7)}-01`);
  };

  const firstOfMonth = new Date(Date.UTC(year, monthIndex, 1));
  const firstWeekday = firstOfMonth.getUTCDay(); // 0=Sun..6=Sat
  const leadingDays = (firstWeekday + 6) % 7; // back up to the Monday on/before the 1st
  const gridStart = new Date(Date.UTC(year, monthIndex, 1 - leadingDays));

  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setUTCDate(gridStart.getUTCDate() + i);
    const dow = d.getUTCDay();
    return {
      dateStr: d.toISOString().slice(0, 10),
      day: d.getUTCDate(),
      month: d.getUTCMonth() + 1,
      inMonth: d.getUTCMonth() === monthIndex,
      isWeekend: dow === 0 || dow === 6,
    };
  });

  // Which statuses occupy the calendar is caller-controlled — the admin
  // calendar shows Approved + Pending (a Disapproved/Cancelled request
  // never actually happens, so it shouldn't visually claim a day), while
  // the personal calendar is Approved-only per how it's called below.
  const relevantLeaves = leaves.filter((l) => statuses.includes(l.status));
  const leavesOnDay = (dateStr) => relevantLeaves.filter((l) => l.dateFrom <= dateStr && dateStr <= l.dateTo);

  // Holidays repeat every year (month/day only, same convention as
  // Holidays.js's own calendar), so they're matched by month+day rather
  // than the full dateStr a leave range is matched by.
  const holidaysOnDay = (cell) => holidays.filter((h) => Number(h.month) === cell.month && Number(h.day) === cell.day);

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm mt-4 overflow-hidden">
      <div className="px-4 sm:px-6 py-3 border-b border-border flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">{monthLabel}</h2>
        <div className="flex items-center gap-1">
          <button
            onClick={() => shiftMonth(-1)}
            className="px-2 py-1 rounded-lg border border-border text-xs font-medium text-ink hover:bg-card-tint transition-colors"
          >
            ‹ Prev
          </button>
          <button
            onClick={() => shiftMonth(1)}
            className="px-2 py-1 rounded-lg border border-border text-xs font-medium text-ink hover:bg-card-tint transition-colors"
          >
            Next ›
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="border-b border-border px-1 py-1.5 text-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-muted"
          >
            {w}
          </div>
        ))}
        {cells.map((cell) => {
          const hits = leavesOnDay(cell.dateStr);
          const visible = hits.slice(0, CALENDAR_CHIP_LIMIT);
          const overflow = hits.length - visible.length;
          const holidayHits = holidaysOnDay(cell);
          return (
            <div
              key={cell.dateStr}
              className={`border-b border-r border-border p-1 sm:p-1.5 min-h-[4.25rem] sm:min-h-[6rem] [&:nth-child(7n+1)]:border-l ${
                !cell.inMonth ? 'bg-card-tint/50' : cell.isWeekend ? 'bg-light-gold/40' : ''
              }`}
            >
              <p className={`text-[10px] sm:text-xs font-medium ${cell.inMonth ? 'text-ink' : 'text-muted/60'}`}>
                {cell.day}
              </p>
              <div className="mt-0.5 flex flex-col gap-0.5">
                {holidayHits.map((h) => (
                  <span
                    key={h.holiday_id}
                    title={h.name}
                    className="block truncate rounded px-1 py-0.5 text-[8px] sm:text-[10px] font-medium leading-tight bg-light-gold text-bronze"
                  >
                    {h.name}
                  </span>
                ))}
                {visible.map((l) => (
                  <span
                    key={l.leaveId}
                    title={`${l.employee} — ${l.leaveType}${l.halfDayPeriod ? ` (${l.halfDayPeriod})` : ''} — ${l.status}`}
                    className={`block truncate rounded px-1 py-0.5 text-[8px] sm:text-[10px] font-medium leading-tight ${
                      l.status === 'Approved' ? 'bg-success-bg text-success-tx' : 'bg-neutral-bg text-neutral-tx'
                    }`}
                  >
                    {chipLabel(l)}
                  </span>
                ))}
                {overflow > 0 && <span className="text-[8px] sm:text-[9px] text-muted">+{overflow} more</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 sm:px-6 py-2.5 border-t border-border flex items-center gap-4 text-[11px] text-muted">
        {statuses.includes('Approved') && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-success-tx" /> Approved
          </span>
        )}
        {statuses.includes('Pending') && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-neutral-tx" /> Pending
          </span>
        )}
        {holidays.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-bronze" /> Holiday
          </span>
        )}
      </div>
    </div>
  );
}
